import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useTranslation } from 'react-i18next';

const FeatureComparisonTable = () => {
  const { t } = useTranslation();
  
  const features = [
    { name: t('platform.feature_1'), holionix: true, traditional: false, competitors: false },
    { name: t('platform.feature_2'), holionix: true, traditional: false, competitors: true },
    { name: t('platform.feature_3'), holionix: true, traditional: false, competitors: true },
    { name: t('platform.feature_4'), holionix: true, traditional: false, competitors: false },
    { name: t('platform.feature_5'), holionix: true, traditional: false, competitors: false },
    { name: t('platform.feature_6'), holionix: true, traditional: false, competitors: false },
    { name: t('platform.feature_7'), holionix: true, traditional: false, competitors: true },
    { name: t('platform.feature_8'), holionix: true, traditional: true, competitors: true },
    { name: t('platform.feature_9'), holionix: true, traditional: false, competitors: true },
    { name: t('platform.feature_10'), holionix: true, traditional: false, competitors: false },
    { name: t('platform.feature_11'), holionix: true, traditional: false, competitors: false },
    { name: t('platform.feature_12'), holionix: true, traditional: true, competitors: true },
  ];

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          {t('platform.comparison_heading')}
        </h2>
        <p className="text-muted-foreground text-lg">
          {t('platform.comparison_subtitle')}
        </p>
      </motion.div>

      <Card className="overflow-hidden glass-effect">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 font-bold">{t('platform.comparison_feature')}</th>
                <th className="p-4 text-center">
                  <div className="font-bold text-lg gradient-brand-text">{t('platform.comparison_holionix')}</div>
                </th>
                <th className="p-4 text-center font-semibold text-muted-foreground">
                  {t('platform.comparison_traditional')}
                </th>
                <th className="p-4 text-center font-semibold text-muted-foreground">
                  {t('platform.comparison_competitors')}
                </th>
              </tr>
            </thead>
            <tbody>
              {features.map((feature, index) => (
                <motion.tr
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="border-b border-border hover:bg-secondary/20 transition-colors"
                >
                  <td className="p-4 font-medium">{feature.name}</td>
                  <td className="p-4">
                    <div className="flex justify-center">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.05 + 0.2 }}
                        className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg"
                      >
                        <Check className="w-5 h-5 text-white" />
                      </motion.div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-center">
                      {feature.traditional ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 + 0.2 }}
                          className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center"
                        >
                          <Check className="w-5 h-5 text-muted-foreground" />
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 + 0.2 }}
                          className="w-8 h-8 rounded-full bg-secondary/50 flex items-center justify-center"
                        >
                          <X className="w-5 h-5 text-muted-foreground" />
                        </motion.div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-center">
                      {feature.competitors ? (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 + 0.2 }}
                          className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center"
                        >
                          <Check className="w-5 h-5 text-muted-foreground" />
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 + 0.2 }}
                          className="w-8 h-8 rounded-full bg-secondary/50 flex items-center justify-center"
                        >
                          <X className="w-5 h-5 text-muted-foreground" />
                        </motion.div>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

export default FeatureComparisonTable;
